import React, { useEffect, useState, useMemo } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { usePopup } from '../context/PopupContext';
import './StudentsPro.css';

const PAGE_SIZE = 15;

const statusPill = (status) => {
  const map = {
    'placed': { bg: '#dcfce7', color: '#166534' },
    'not placed': { bg: '#fee2e2', color: '#991b1b' },
    'in process': { bg: '#fef9c3', color: '#854d0e' }
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#64748b' };
  return <span className="smx-pill" style={{ background: s.bg, color: s.color }}>{status || 'unknown'}</span>;
};

const Students = () => {
  const navigate = useNavigate();
  const { showConfirm, showAlert } = usePopup();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [branch, setBranch] = useState('all');
  const [batch, setBatch] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => { fetchStudents(); }, []);

  const fetchStudents = async () => {
    setLoading(true); setError('');
    try {
      const res = await API.get('/students');
      setStudents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load students.');
    } finally {
      setLoading(false);
    }
  };

  // Derive filter options from real data (no hardcoded branch list).
  const branches = useMemo(() => [...new Set(students.map(s => s.branch).filter(Boolean))].sort(), [students]);
  const batches = useMemo(() => [...new Set(students.map(s => s.placement_batch_year).filter(Boolean))].sort((a, b) => b - a), [students]);
  const statuses = useMemo(() => [...new Set(students.map(s => s.placement_status).filter(Boolean))].sort(), [students]);

  const filtered = useMemo(() => {
    let data = students.filter(s => {
      if (search) {
        const q = search.toLowerCase();
        if (!(s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) || s.roll_number?.toLowerCase().includes(q))) return false;
      }
      if (branch !== 'all' && s.branch !== branch) return false;
      if (batch !== 'all' && String(s.placement_batch_year) !== String(batch)) return false;
      if (status !== 'all' && s.placement_status !== status) return false;
      return true;
    });
    const { key, dir } = sort;
    data = [...data].sort((a, b) => {
      let av = a[key] ?? '', bv = b[key] ?? '';
      if (key === 'cgpa' || key === 'arrears' || key === 'year_of_study') { av = Number(av) || 0; bv = Number(bv) || 0; }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [students, search, branch, batch, status, sort]);

  // Reset page and clear selection when the visible set changes, so bulk actions
  // can never act on rows the admin can no longer see.
  useEffect(() => { setPage(1); setSelected([]); }, [search, branch, batch, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = useMemo(() => {
    const withCgpa = students.filter(s => Number(s.cgpa) > 0);
    return {
      total: students.length,
      placed: students.filter(s => s.placement_status === 'placed').length,
      notPlaced: students.filter(s => s.placement_status === 'not placed').length,
      arrears: students.filter(s => Number(s.arrears) > 0).length,
      avgCgpa: withCgpa.length ? (withCgpa.reduce((a, s) => a + Number(s.cgpa), 0) / withCgpa.length).toFixed(2) : '—',
      branches: branches.length
    };
  }, [students, branches]);

  const toggleSort = (key) => setSort(p => p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const arrow = (key) => sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  // Accessible, keyboard-operable sortable column header.
  const ariaSort = (key) => (sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');
  const sortProps = (key, extra = '') => ({
    className: `sortable ${extra}`.trim(), scope: 'col', role: 'columnheader', tabIndex: 0, 'aria-sort': ariaSort(key),
    onClick: () => toggleSort(key),
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(key); } }
  });

  const clearAll = () => { setSearch(''); setBranch('all'); setBatch('all'); setStatus('all'); };
  const activeFilters = (search ? 1 : 0) + (branch !== 'all' ? 1 : 0) + (batch !== 'all' ? 1 : 0) + (status !== 'all' ? 1 : 0);

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const pageIds = pageRows.map(s => s.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.includes(id));
  const toggleSelectPage = () => setSelected(p => allPageSelected ? p.filter(id => !pageIds.includes(id)) : [...new Set([...p, ...pageIds])]);

  const handleDelete = async (id, name) => {
    if (!(await showConfirm('Delete Student', `Delete ${name || 'this student'}? This deactivates the account.`))) return;
    try { await API.delete(`/students/${id}`); setSelected(p => p.filter(x => x !== id)); fetchStudents(); }
    catch { showAlert('Error', 'Failed to delete student'); }
  };

  const handleBulkDelete = async () => {
    if (!(await showConfirm('Delete Selected', `Delete ${selected.length} selected student(s)?`))) return;
    try { await API.delete('/students/bulk', { data: { ids: selected } }); setSelected([]); fetchStudents(); }
    catch { showAlert('Error', 'Failed to delete selected students'); }
  };

  return (
    <div className="smx-page">
      <div className="smx-top">
        <div>
          <h1>Student Management</h1>
          <p>{filtered.length} of {students.length} students{activeFilters > 0 && <button className="smx-clear" onClick={clearAll}> · Clear ({activeFilters})</button>}</p>
        </div>
        <div className="smx-actions">
          {selected.length > 0 && <button className="smx-btn danger" onClick={handleBulkDelete}>Delete Selected ({selected.length})</button>}
          <button className="smx-btn" onClick={() => navigate('/admin/students/add')}>Bulk Upload</button>
          <button className="smx-btn primary" onClick={() => navigate('/admin/students/add')}>+ Add Student</button>
        </div>
      </div>

      <div className="smx-stats">
        <div className="smx-stat"><div className="v">{stats.total}</div><div className="l">Total</div></div>
        <div className="smx-stat"><div className="v" style={{ color: '#16a34a' }}>{stats.placed}</div><div className="l">Placed</div></div>
        <div className="smx-stat"><div className="v" style={{ color: '#dc2626' }}>{stats.notPlaced}</div><div className="l">Not Placed</div></div>
        <div className="smx-stat"><div className="v" style={{ color: '#d97706' }}>{stats.arrears}</div><div className="l">With Arrears</div></div>
        <div className="smx-stat"><div className="v">{stats.avgCgpa}</div><div className="l">Avg CGPA</div></div>
        <div className="smx-stat"><div className="v">{stats.branches}</div><div className="l">Branches</div></div>
      </div>

      <div className="smx-toolbar">
        <input className="smx-search" type="search" aria-label="Search students by name, email or roll number" placeholder="Search by name, email, roll no…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="smx-select" value={branch} onChange={e => setBranch(e.target.value)}><option value="all">All Branches</option>{branches.map(b => <option key={b} value={b}>{b}</option>)}</select>
        <select className="smx-select" value={batch} onChange={e => setBatch(e.target.value)}><option value="all">All Batches</option>{batches.map(b => <option key={b} value={b}>{b}</option>)}</select>
        <select className="smx-select" value={status} onChange={e => setStatus(e.target.value)}><option value="all">All Status</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}</select>
      </div>

      {loading ? (
        <div className="smx-table-wrap" style={{ padding: 16 }}>{[...Array(8)].map((_, i) => <div key={i} className="smx-skel" />)}</div>
      ) : error ? (
        <div className="smx-table-wrap"><div className="smx-state">⚠️ {error}<br /><button className="smx-btn" style={{ marginTop: 12 }} onClick={fetchStudents}>Retry</button></div></div>
      ) : (
        <div className="smx-table-wrap">
          <div style={{ overflowX: 'auto' }}>
            <table className="smx-table">
              <thead>
                <tr>
                  <th style={{ width: 34 }} scope="col"><input type="checkbox" aria-label="Select all students on this page" checked={allPageSelected} onChange={toggleSelectPage} /></th>
                  <th {...sortProps('name')}>Name{arrow('name')}</th>
                  <th {...sortProps('roll_number')}>Roll No{arrow('roll_number')}</th>
                  <th className="smx-hide-sm" scope="col">Branch</th>
                  <th className="smx-hide-sm" scope="col">Batch</th>
                  <th {...sortProps('cgpa', 'smx-hide-sm')}>CGPA{arrow('cgpa')}</th>
                  <th {...sortProps('arrears', 'smx-hide-sm')}>Arrears{arrow('arrears')}</th>
                  <th className="smx-hide-sm" scope="col">Email</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={10}><div className="smx-state">No students match your filters.</div></td></tr>
                ) : pageRows.map(s => (
                  <tr key={s.id}>
                    <td><input type="checkbox" aria-label={`Select ${s.name}`} checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} /></td>
                    <td><span className="smx-name" onClick={() => navigate(`/admin/students/${s.id}`)}>{s.name}</span></td>
                    <td>{s.roll_number || '—'}</td>
                    <td className="smx-hide-sm">{s.branch ? <span className="smx-badge">{s.branch}</span> : '—'}</td>
                    <td className="smx-hide-sm">{s.placement_batch_year || '—'}</td>
                    <td className="smx-hide-sm" style={{ fontWeight: 600 }}>{Number(s.cgpa) > 0 ? Number(s.cgpa).toFixed(2) : '—'}</td>
                    <td className="smx-hide-sm" style={{ color: Number(s.arrears) > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{s.arrears ?? 0}</td>
                    <td className="smx-hide-sm" style={{ color: '#64748b' }}>{s.email}</td>
                    <td>{statusPill(s.placement_status)}</td>
                    <td>
                      <div className="smx-row-actions">
                        <button className="smx-icon-btn" onClick={() => navigate(`/admin/students/${s.id}`)}>View</button>
                        <button className="smx-icon-btn" onClick={() => navigate(`/admin/students/edit/${s.id}`)}>Edit</button>
                        <button className="smx-icon-btn del" onClick={() => handleDelete(s.id, s.name)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="smx-pagination">
            <span>Page {page} of {totalPages} · {filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
            <div className="smx-page-btns">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
